import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, X, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const PASSWORD_RULES = [
  { id: "len", label: "At least 8 characters", test: (p) => p.length >= 8 },
  { id: "upper", label: "1 uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { id: "num", label: "1 number", test: (p) => /\d/.test(p) },
  { id: "special", label: "1 special character", test: (p) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(p) },
  // P3 #12: explicit no-spaces rule for password
  { id: "nospace", label: "No spaces", test: (p) => p.length > 0 && !/\s/.test(p) },
];

// P3 #12: letters + spaces (spaces are now allowed for Name)
const NAME_REGEX = /^[A-Za-z ]*$/;

// P3 #12: letters + spaces + hyphens/ampersands/apostrophes/periods
const ORG_REGEX = /^[A-Za-z\-&'. ]*$/;

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();

  const [form, setForm] = useState({ full_name: "", email: "", password: "", org_name: "", acceptTerms: false, });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [pwTouched, setPwTouched] = useState(false);
  const [showPw, setShowPw] = useState(false);

  /* ── password rule states ── */
  const ruleStates = useMemo(
    () => PASSWORD_RULES.map((r) => ({ ...r, pass: r.test(form.password) })),
    [form.password]
  );
  const allPwValid = ruleStates.every((r) => r.pass);

  /* ── field-level validation ── */
  const validateField = (key, value) => {
    switch (key) {
      case "full_name":
        if (!value.trim()) return "Full name is required.";
        if (!NAME_REGEX.test(value)) return "Name may only contain letters and spaces.";
        if (value.trim().length < 2) return "Name must be at least 2 characters.";
        return "";

      case "org_name":
        if (!value.trim()) return "Organisation name is required.";
        if (!ORG_REGEX.test(value)) return "Organisation name may only contain letters, spaces, hyphens, ampersands, apostrophes, and periods.";
        if (value.trim().length < 2) return "Organisation name must be at least 2 characters.";
        return "";

      case "email":
        if (!value.trim()) return "Email is required.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Enter a valid email address.";
        return "";

      default:
        return "";
    }
  };

  /* ── controlled update with inline validation ── */
  const update = (key) => (e) => {
    const value =
      key === "acceptTerms" ? e.target.checked : e.target.value;

    if (key === "full_name" && !NAME_REGEX.test(value)) return;
    if (key === "org_name" && !ORG_REGEX.test(value)) return;
    if (key === "password" && /\s/.test(value)) return;

    setForm((prev) => ({ ...prev, [key]: value }));

    if (errors[key] !== undefined) {
      setErrors((prev) => ({
        ...prev,
        [key]: key === "acceptTerms"
          ? value
            ? ""
            : "You must accept the Terms & Conditions."
          : validateField(key, value),
      }));
    }
  };

  const blur = (key) => () => {
    setErrors((prev) => ({ ...prev, [key]: validateField(key, form[key]) }));
  };

  /* ── submit ── */
  const submit = async (e) => {
    e.preventDefault();
    setPwTouched(true);

    const fieldErrors = {
      full_name: validateField("full_name", form.full_name),
      email: validateField("email", form.email),
      org_name: validateField("org_name", form.org_name),
    };
    setErrors(fieldErrors);

    fieldErrors.acceptTerms = form.acceptTerms
      ? ""
      : "You must accept the Terms & Conditions.";

    const hasFieldError = Object.values(fieldErrors).some(Boolean);
    if (hasFieldError || !allPwValid) {
      toast.error("Please fix the errors before continuing.");
      return;
    }

    setBusy(true);
    try {
      await signup(form);
      toast.success("Account created");
      nav("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign up failed");
    } finally {
      setBusy(false);
    }
  };

  /* ── helpers ── */
  const inputClass = (key) =>
    `mt-2 w-full px-4 py-2.5 rounded-md border focus:outline-none focus:ring-2 focus:ring-[#0F1F3D] ${errors[key] ? "border-[#EF4444]" : "border-[#E2E8F0]"
    }`;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA] p-6">
      <div className="max-w-md w-full bg-white rounded-xl border border-[#E2E8F0] p-8">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 mb-5">
          <div className="h-8 w-8 rounded-md bg-[#0F1F3D] flex items-center justify-center">
            <div className="h-2.5 w-2.5 rounded-full bg-[#10B981]" />
          </div>
          <span className="font-display font-bold text-lg text-[#0F1F3D]">LaunchRadar</span>
        </Link>

        <h2 className="font-display text-3xl font-bold text-[#0F1F3D]">Create account</h2>
        <p className="text-sm text-[#475569] mt-2">Spin up a new organisation in seconds.</p>

        <form onSubmit={submit} noValidate className="mt-5 space-y-5">
          {/* Full name */}
          <div className="!mt-[10px]">
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">
              Full name
            </label>
            <input
              required
              value={form.full_name}
              onChange={update("full_name")}
              onBlur={blur("full_name")}
              placeholder="Jane Smith"
              data-testid="signup-name-input"
              className={inputClass("full_name")}
            />
            {errors.full_name && (
              <p className="mt-1 text-xs text-[#EF4444]" data-testid="signup-name-error">
                {errors.full_name}
              </p>
            )}
          </div>

          {/* Work email */}
          <div className="!mt-[10px]">
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">
              Work email
            </label>
            <input
              type="email"
              required
              value={form.email}
              onChange={update("email")}
              onBlur={blur("email")}
              placeholder="jane@company.com"
              data-testid="signup-email-input"
              className={inputClass("email")}
            />
            {errors.email && (
              <p className="mt-1 text-xs text-[#EF4444]" data-testid="signup-email-error">
                {errors.email}
              </p>
            )}
          </div>

          {/* Organisation name */}
          <div className="!mt-[10px]">
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">
              Organisation name
            </label>
            <input
              required
              value={form.org_name}
              onChange={update("org_name")}
              onBlur={blur("org_name")}
              placeholder="Acme Corp"
              data-testid="signup-org-input"
              className={inputClass("org_name")}
            />
            {errors.org_name && (
              <p className="mt-1 text-xs text-[#EF4444]" data-testid="signup-org-error">
                {errors.org_name}
              </p>
            )}
          </div>

          {/* Password */}
          <div className="!mt-[10px]">
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">
              Password
            </label>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                required
                value={form.password}
                onChange={update("password")}
                onBlur={() => setPwTouched(true)}
                data-testid="signup-password-input"
                className="mt-2 w-full px-4 py-2.5 pr-11 rounded-md border border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#0F1F3D]"
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                aria-label={showPw ? "Hide password" : "Show password"}
                data-testid="toggle-password-visibility"
                className="absolute right-3 top-1/2 -translate-y-1/2 mt-1 text-[#94A3B8] hover:text-[#475569] transition-colors"
              >
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>

            {/* Password rules */}
            <ul className="mt-2 space-y-1.5" data-testid="signup-password-rules">
              {ruleStates.map((r) => {
                const state = form.password ? (r.pass ? "ok" : "bad") : "idle";
                return (
                  <li
                    key={r.id}
                    data-testid={`pw-rule-${r.id}-${state}`}
                    className="flex items-center gap-2 text-xs"
                  >
                    {state === "ok" ? (
                      <Check className="h-3.5 w-3.5 text-[#10B981]" />
                    ) : state === "bad" ? (
                      <X className="h-3.5 w-3.5 text-[#EF4444]" />
                    ) : (
                      <span className="h-3.5 w-3.5 rounded-full border border-[#CBD5E1]" />
                    )}
                    <span
                      className={
                        state === "ok"
                          ? "text-[#047857]"
                          : state === "bad"
                            ? "text-[#B91C1C]"
                            : "text-[#64748B]"
                      }
                    >
                      {r.label}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="!mt-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.acceptTerms}
                onChange={update("acceptTerms")}
                className="mt-1 h-4 w-4 rounded border-[#CBD5E1] text-[#0F1F3D] focus:ring-[#0F1F3D]"
                data-testid="signup-terms-checkbox"
              />

              <span className="text-sm text-[#475569] leading-6">
                I agree to the{" "}
                <Link
                  to="/terms-and-conditions"
                  target="_blank"
                  className="font-medium text-[#0F1F3D] underline underline-offset-4 hover:text-[#10B981]"
                >
                  Terms &amp; Conditions
                </Link>{" "}
                and{" "}
                <Link
                  to="/privacy-policy"
                  target="_blank"
                  className="font-medium text-[#0F1F3D] underline underline-offset-4 hover:text-[#10B981]"
                >
                  Privacy Policy
                </Link>
                .
              </span>
            </label>

            {errors.acceptTerms && (
              <p className="mt-2 ml-7 text-xs text-[#EF4444]">
                {errors.acceptTerms}
              </p>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={busy || !allPwValid || !form.acceptTerms}
            data-testid="signup-submit-button"
            className="w-full px-4 py-2.5 rounded-md bg-[#0F1F3D] text-white font-medium hover:bg-[#172A4F] disabled:opacity-60 transition-colors"
          >
            {busy ? "Creating..." : "Create account"}
          </button>
        </form>

        <div className="mt-4 text-sm text-[#475569]">
          Already have an account?{" "}
          <Link
            to="/login"
            data-testid="goto-login-link"
            className="text-[#0F1F3D] font-medium hover:underline"
          >
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}